import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  ADMIN_DEPARTMENTS,
  ADMIN_ROLES,
  AdminDepartment,
  AdminRole,
  DEPARTMENT_LABELS,
  DEPARTMENT_OPTIONS,
  ROLE_LABELS,
} from '../../utils/adminPermissions';
import { AdminZoneOption, createAdminStaff } from '../../services/admin';
import AdminZoneAutocomplete from './AdminZoneAutocomplete';

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  showSnackbar: (msg: string, type: 'success' | 'error') => void;
}

export const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  onClose,
  onCreated,
  showSnackbar,
}) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    departments: [ADMIN_DEPARTMENTS.SERVICE] as AdminDepartment[],
    role: ADMIN_ROLES.TRAINEE as AdminRole,
    location: null as AdminZoneOption | null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      password: '',
      departments: [ADMIN_DEPARTMENTS.SERVICE],
      role: ADMIN_ROLES.TRAINEE,
      location: null,
    });
    setShowPassword(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      showSnackbar('Name, email and password are required', 'error');
      return;
    }

    if (form.departments.length === 0) {
      showSnackbar('At least one department is required', 'error');
      return;
    }

    if (!form.location) {
      showSnackbar('Location is required', 'error');
      return;
    }

    try {
      setSubmitting(true);
      await createAdminStaff({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        department: form.departments[0],
        departments: form.departments,
        role: form.role,
        locationZoneId: form.location._id,
        locationName: form.location.name,
      });
      showSnackbar('Admin user created successfully', 'success');
      resetForm();
      onCreated();
      onClose();
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to create admin user', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { bgcolor: '#171717', border: '1px solid #242424', color: '#fff' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <PersonAddIcon sx={{ color: '#3b82f6' }} />
        <Typography variant="h6" flex={1}>
          Create Admin Role
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: '#94a3b8' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="#64748b" mb={2}>
          New users start with default permissions for their role. You can customize
          permissions after creation.
        </Typography>
        <Box display="grid" gap={2}>
          <TextField
            label="Full Name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            fullWidth
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Departments"
            value={form.departments}
            onChange={(e) => {
              const value = e.target.value;
              const next = (typeof value === 'string' ? value.split(',') : value) as AdminDepartment[];
              setForm((p) => ({ ...p, departments: next }));
            }}
            fullWidth
            size="small"
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (
                <Box display="flex" gap={0.5} flexWrap="wrap">
                  {(selected as AdminDepartment[]).map((dept) => (
                    <Chip key={dept} label={DEPARTMENT_LABELS[dept]} size="small" />
                  ))}
                </Box>
              ),
            }}
          >
            {DEPARTMENT_OPTIONS.map((dept) => (
              <MenuItem key={dept} value={dept}>
                <Checkbox size="small" checked={form.departments.includes(dept)} />
                <ListItemText primary={DEPARTMENT_LABELS[dept]} />
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Role"
            value={form.role}
            onChange={(e) =>
              setForm((p) => ({ ...p, role: e.target.value as AdminRole }))
            }
            fullWidth
            size="small"
          >
            {Object.values(ADMIN_ROLES).map((role) => (
              <MenuItem key={role} value={role}>
                {ROLE_LABELS[role]}
              </MenuItem>
            ))}
          </TextField>
          <AdminZoneAutocomplete
            value={form.location}
            onChange={(location) => setForm((p) => ({ ...p, location }))}
            showSnackbar={showSnackbar}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} sx={{ color: '#94a3b8' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={submitting}
        >
          {submitting ? 'Creating...' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;
